import React, { useEffect, useMemo, useState } from "react";
import { LayoutDashboard, Users, Car, Map, Calendar, BarChart3, Plug, Search, Mail, Phone, Pencil, Power, Check, X } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const navItems = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Conductores", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/usuarios", label: "Usuarios", icon: Users },
  { to: "/admin/integraciones", label: "Integraciones", icon: Plug },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];
const bottomNav = [
  { to: "/admin", label: "Panel", icon: LayoutDashboard, end: true },
  { to: "/admin/reservas", label: "Reservas", icon: Calendar },
  { to: "/admin/conductores", label: "Choferes", icon: Car },
  { to: "/admin/rutas", label: "Rutas", icon: Map },
  { to: "/admin/reportes", label: "Reportes", icon: BarChart3 },
];

const EMPTY_EDIT = { full_name: "", email: "", phone: "" };

const AdminUsers = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [rowError, setRowError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/admin/clientes");
        setClients(data);
      } catch {
        setError("No se pudieron cargar los clientes.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({ full_name: c.full_name, email: c.email, phone: c.phone || "" });
    setRowError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setRowError("");
  };

  const saveEdit = async (id) => {
    setSaving(true);
    setRowError("");
    try {
      const { data } = await api.patch(`/api/admin/clientes/${id}`, editForm);
      setClients((prev) => prev.map((c) => (c.id === id ? data : c)));
      setEditingId(null);
    } catch (err) {
      setRowError(err.response?.data?.detail || "No se pudo guardar los cambios.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    setRowError("");
    try {
      const { data } = await api.patch(`/api/admin/clientes/${c.id}/estado`, { is_active: !c.is_active });
      setClients((prev) => prev.map((x) => (x.id === c.id ? data : x)));
    } catch (err) {
      setRowError(err.response?.data?.detail || "No se pudo cambiar el estado del cliente.");
    }
  };

  const stats = useMemo(() => {
    const totalTrips = clients.reduce((acc, c) => acc + c.trips_count, 0);
    const totalRevenue = clients.reduce((acc, c) => acc + Number(c.total_spent), 0);
    return {
      total: clients.length,
      active: clients.filter((c) => c.is_active).length,
      totalTrips,
      totalRevenue,
    };
  }, [clients]);

  const filtered = clients.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.full_name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
  });

  return (
    <PanelShell role="Admin" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} userMeta="Administrador" testId="admin-users">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">Base de clientes</p>
        <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">Usuarios</h1>

        {error && <p className="mt-4 text-xs text-red-400 font-body" data-testid="admin-users-error">{error}</p>}
        {rowError && <p className="mt-4 text-xs text-red-400 font-body" data-testid="admin-users-row-error">{rowError}</p>}

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Clientes" value={stats.total} tone="gold" />
          <StatCard label="Clientes activos" value={stats.active} tone="light" />
          <StatCard label="Viajes totales" value={stats.totalTrips} tone="gold" />
          <StatCard label="Ingresos totales" value={`S/ ${stats.totalRevenue.toLocaleString("es-PE", { minimumFractionDigits: 2 })}`} tone="gold" />
        </div>

        <div className="mt-6 bg-[#1C1D20] border border-[#2E2F33] rounded-xl overflow-hidden">
          <div className="p-4 border-b border-[#2E2F33] flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 bg-[#0D0E10] border border-[#2E2F33] focus-within:border-[#C9A227] rounded-lg px-3.5 py-2.5">
              <Search size={14} className="text-[#C9A227]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar cliente..."
                className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F5] font-body"
                data-testid="users-search"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display border-b border-[#2E2F33]">
                  <th className="text-left px-5 py-3 font-normal">Cliente</th>
                  <th className="text-left px-5 py-3 font-normal">Contacto</th>
                  <th className="text-left px-5 py-3 font-normal">Viajes</th>
                  <th className="text-left px-5 py-3 font-normal">Gastado</th>
                  <th className="text-left px-5 py-3 font-normal">Estado</th>
                  <th className="text-left px-5 py-3 font-normal">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {!loading &&
                  filtered.map((c) => {
                    const isEditing = editingId === c.id;
                    return (
                      <tr key={c.id} className="border-b border-[#2E2F33] hover:bg-[#0D0E10]/50 transition" data-testid={`user-row-${c.id}`}>
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <input
                              value={editForm.full_name}
                              onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                              className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-2 text-sm text-[#F5F5F5] font-body outline-none"
                              data-testid={`user-edit-name-${c.id}`}
                            />
                          ) : (
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-[#C9A227]/15 border border-[#C9A227]/40 flex items-center justify-center text-[#C9A227] font-display font-bold text-xs">
                                {c.full_name.split(" ").map((s) => s[0]).join("").slice(0, 2)}
                              </div>
                              <p className="font-body">{c.full_name}</p>
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-xs text-[#A0A0A5]">
                          {isEditing ? (
                            <div className="space-y-1.5">
                              <input
                                value={editForm.email}
                                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                                className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-1.5 text-xs text-[#F5F5F5] font-body outline-none"
                                data-testid={`user-edit-email-${c.id}`}
                              />
                              <input
                                value={editForm.phone}
                                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                                className="w-full bg-[#0D0E10] border border-[#2E2F33] focus:border-[#C9A227] rounded-lg px-3 py-1.5 text-xs text-[#F5F5F5] font-body outline-none"
                                data-testid={`user-edit-phone-${c.id}`}
                              />
                            </div>
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5"><Mail size={11} className="text-[#C9A227]" /> {c.email}</div>
                              {c.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone size={11} className="text-[#C9A227]" /> {c.phone}</div>}
                            </>
                          )}
                        </td>
                        <td className="px-5 py-4 font-body">{c.trips_count}</td>
                        <td className="px-5 py-4 font-display font-bold text-[#C9A227]">S/ {c.total_spent}</td>
                        <td className="px-5 py-4"><StatusBadge status={c.is_active ? "Activo" : "Descanso"} /></td>
                        <td className="px-5 py-4">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => saveEdit(c.id)}
                                disabled={saving}
                                className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded-md disabled:opacity-60"
                                title="Guardar"
                                data-testid={`user-save-${c.id}`}
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-1.5 text-[#A0A0A5] hover:bg-[#2E2F33] rounded-md"
                                title="Cancelar"
                                data-testid={`user-cancel-${c.id}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => startEdit(c)}
                                className="p-1.5 text-[#A0A0A5] hover:text-[#C9A227] hover:bg-[#C9A227]/10 rounded-md"
                                title="Editar"
                                data-testid={`user-edit-${c.id}`}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                onClick={() => toggleStatus(c)}
                                className={`p-1.5 rounded-md ${c.is_active ? "text-red-400 hover:bg-red-500/10" : "text-emerald-400 hover:bg-emerald-500/10"}`}
                                title={c.is_active ? "Desactivar" : "Activar"}
                                data-testid={`user-toggle-status-${c.id}`}
                              >
                                <Power size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {loading && <p className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">Cargando…</p>}
          {!loading && filtered.length === 0 && (
            <p className="px-5 py-8 text-center text-xs text-[#A0A0A5] font-body">No hay clientes que mostrar.</p>
          )}
        </div>
      </div>
    </PanelShell>
  );
};

const StatCard = ({ label, value, tone }) => (
  <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-5">
    <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">{label}</p>
    <p className={`font-display font-bold text-2xl mt-2 ${tone === "gold" ? "text-[#C9A227]" : "text-[#F5F5F5]"}`}>{value}</p>
  </div>
);

export default AdminUsers;
